import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../db';
import { auth } from '../auth';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';

const ADMIN_EMAIL = 'u012me@gmail.com';

const PLAN_OPTIONS = [
  { value: 'free',      label: '무료',       color: '#6b7280', bg: '#f3f4f6' },
  { value: 'pro',       label: '프로',       color: '#EA580C', bg: '#FAFAFA' },
  { value: 'franchise', label: '프랜차이즈', color: '#7C3AED', bg: '#f5f3ff' },
];

import AdminDataAssets from './AdminDataAssets';

function PlanApproveRow({ req, onApprove, onReject, saving, isRejected }) {
  const [selectedPlan, setSelectedPlan] = useState('free');
  return (
    <div style={{ borderTop:'1px solid #e5e7eb', marginTop:'12px', paddingTop:'12px',
                  display:'flex', flexDirection:'column', gap:'10px' }}>
      <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'center' }}>
        <span style={{ fontSize:'12px', color:'#6b7280', fontWeight:'700', marginRight:'4px' }}>플랜 선택:</span>
        {PLAN_OPTIONS.map(p => (
          <button
            key={p.value}
            onClick={() => setSelectedPlan(p.value)}
            style={{
              padding:'6px 14px', borderRadius:'8px', cursor:'pointer', fontSize:'12px', fontWeight:'700',
              border: selectedPlan === p.value ? `2px solid ${p.color}` : '2px solid transparent',
              background: selectedPlan === p.value ? p.color : p.bg,
              color: selectedPlan === p.value ? '#fff' : p.color,
            }}>
            {p.label}
          </button>
        ))}
      </div>
      <div style={{ display:'flex', gap:'8px' }}>
        <button
          onClick={() => onApprove(req, selectedPlan)}
          disabled={saving}
          style={{
            padding:'10px 22px', borderRadius:'8px', border:'none', cursor: saving ? 'not-allowed' : 'pointer',
            background:'#FF6F0F', color:'#fff', fontSize:'14px', fontWeight:'800',
            opacity: saving ? 0.5 : 1,
          }}>
          ✅ {isRejected ? '다시 승인' : '승인'}
        </button>
        {!isRejected && (
          <button onClick={() => onReject(req)}
            style={{ padding:'10px 20px', borderRadius:'8px', border:'none', cursor:'pointer',
                     background:'#F4F4F5', color:'#52525B', fontSize:'14px', fontWeight:'700' }}>
            ✕ 거절
          </button>
        )}
      </div>
    </div>
  );
}

function AdminDashboard({ user, onBack }) {
  const [data, setData]       = useState({ global: {}, months: [], ingredients: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('overview'); // overview | ingredients | prices | allowlist | migrate
  const [migrateStatus, setMigrateStatus] = useState('');
  const [importData,    setImportData]    = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [allowlist, setAllowlist] = useState({ emails: [], open: false });
  const [newEmail, setNewEmail]   = useState('');
  const [allowSaving, setAllowSaving] = useState(false);
  const [users,     setUsers]     = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [purging, setPurging] = useState(null);   // V106: 삭제 중인 uid
  const [userSearch,   setUserSearch]   = useState('');

  useEffect(() => { loadStats(); loadAllowlist(); loadUsers(); }, []);

  const loadAllowlist = async () => {
    try {
      const snap = await getDoc(doc(db, 'config', 'allowlist'));
      if (snap.exists()) setAllowlist(snap.data());
    } catch (e) { console.error('allowlist 로드 실패:', e); }
  };

  const saveAllowlist = async (next) => {
    setAllowSaving(true);
    try {
      await setDoc(doc(db, 'config', 'allowlist'), next);
      setAllowlist(next);
    } catch (e) { console.error('allowlist 저장 실패:', e); }
    setAllowSaving(false);
  };

  const addEmail = () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || allowlist.emails.includes(trimmed)) { setNewEmail(''); return; }
    saveAllowlist({ ...allowlist, emails: [...allowlist.emails, trimmed] });
    setNewEmail('');
  };

  const removeEmail = (email) => {
    saveAllowlist({ ...allowlist, emails: allowlist.emails.filter(e => e !== email) });
  };


  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const list = [];
      snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
      list.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      setUsers(list);
    } catch (e) { console.error('가입자 목록 로드 실패:', e); }
    setUsersLoading(false);
  };

  // 사용자 plan 인라인 변경 — admin 전용
  const updateUserPlan = async (uid, newPlan) => {
    try {
      await setDoc(doc(db, 'users', uid), {
        subscription: {
          plan: newPlan,
          status: 'active',
          grantedAt: new Date().toISOString(),
          grantedBy: 'admin',
        }
      }, { merge: true });
      // 로컬 state 즉시 업데이트
      setUsers(prev => prev.map(u => u.uid === uid
        ? { ...u, subscription: { ...(u.subscription || {}), plan: newPlan, status: 'active' } }
        : u));
    } catch (e) {
      console.error('plan 변경 실패:', e);
      alert('플랜 변경 실패: ' + e.message);
    }
  };

  // ── V106: 가입자 승인 / 승인 취소 / 데이터 삭제 ────────────────────────
  // 승인 목록(config/allowlist)이 매장 사장의 유일한 관문이다.
  // 사장이 자기 설정에서 초대한 매니저·직원은 초대장으로 통과하므로
  // 여기 목록에 넣을 필요가 없다.
  const isApproved = (email) => {
    const e = String(email || '').trim().toLowerCase();
    if (!e) return false;
    if (e === ADMIN_EMAIL) return true;
    return (allowlist.emails || []).some(x => String(x || '').trim().toLowerCase() === e);
  };

  const approveUser = async (email) => {
    const e = String(email || '').trim();
    if (!e || isApproved(e)) return;
    await saveAllowlist({ ...allowlist, emails: [...(allowlist.emails || []), e] });
  };

  const revokeUser = async (email) => {
    const e = String(email || '').trim().toLowerCase();
    if (!e || e === ADMIN_EMAIL) return;
    if (!window.confirm(`${email} 의 접근을 차단합니다.\n\n다음 로그인부터 막히며, 데이터는 지워지지 않습니다.\n계속할까요?`)) return;
    await saveAllowlist({
      ...allowlist,
      emails: (allowlist.emails || []).filter(x => String(x || '').trim().toLowerCase() !== e),
    });
  };

  // 되돌릴 수 없다. 2단 확인 후에만 실행.
  const purgeUserData = async (u) => {
    if (!u?.uid || u.email === ADMIN_EMAIL) return;
    if (!window.confirm(
      `⚠️ ${u.email} 의 모든 데이터를 삭제합니다.\n\n` +
      `단가표·레시피·거래장부·발주내역·재고가 전부 지워지며 복구할 수 없습니다.\n\n계속할까요?`
    )) return;
    const typed = window.prompt('정말 삭제하려면 아래에 삭제 라고 입력하세요.');
    if (String(typed || '').trim() !== '삭제') { alert('취소했습니다.'); return; }

    setPurging(u.uid);
    const subcollections = [
      'transactions', 'categories', 'prices', 'itemPrices',
      'orderHistory', 'inventory', 'employees', 'schedules',
      'profitLoss', 'recipes', 'purchaseLog', 'taxData',
      'goals', 'menus', 'groupBuyInterest', 'managers', 'partners', 'snapshots', 'receivings', 'receivingPhotos',
    ];
    try {
      for (const sub of subcollections) {
        try {
          const snap = await getDocs(collection(db, 'users', u.uid, sub));
          for (const d of snap.docs) await deleteDoc(d.ref);
        } catch (e) { console.warn(`${sub} 삭제 실패(계속):`, e.message); }
      }
      try { await deleteDoc(doc(db, 'users', u.uid)); }
      catch (e) { console.warn('본 문서 삭제 실패:', e.message); }

      // 접근도 같이 차단 (데이터만 지우고 로그인이 되면 빈 계정으로 재생성된다)
      await saveAllowlist({
        ...allowlist,
        emails: (allowlist.emails || []).filter(
          x => String(x || '').trim().toLowerCase() !== String(u.email || '').trim().toLowerCase()
        ),
      });

      setUsers(prev => prev.filter(x => x.uid !== u.uid));
      alert(`${u.email} 데이터를 삭제하고 접근을 차단했습니다.`);
    } catch (e) {
      alert('삭제 중 오류: ' + e.message);
    } finally {
      setPurging(null);
    }
  };



  const loadStats = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'stats'));
      let global = {}, months = [], ingredients = [];
      snap.forEach(d => {
        const id = d.id, v = d.data();
        if (id === 'global')         global = v;
        else if (id.startsWith('month_'))  months.push({ id, ...v });
        else if (id.startsWith('ing_'))    ingredients.push({ id, ...v });
        // active_uid_month 문서는 무시
      });
      months.sort((a,b) => (a.month||'').localeCompare(b.month||''));
      ingredients.sort((a,b) => (b.orderCount||0) - (a.orderCount||0));
      setData({ global, months, ingredients });
    } catch(e) { console.error('통계 로드 실패:', e); }
    setLoading(false);
  };

  // 이번 달
  const thisMonth = new Date().toISOString().slice(0,7);
  const thisMonthData   = data.months.find(m => m.month === thisMonth) || {};
  const activeThisMonth = data.global[`activeUsers_${thisMonth}`] || 0;

  // 월별 차트용 최근 6개월
  const recentMonths = data.months.slice(-6);
  const maxOrders    = Math.max(...recentMonths.map(m => m.orderCount||0), 1);

  const card = (emoji, label, value, sub, color='#FF6F0F') => (
    <div style={{ background:'#fff', borderRadius:'14px', padding:'20px 22px',
                  border:'1px solid #e5e7eb', boxShadow:'0 1px 4px rgba(0,0,0,0.05)', flex:1, minWidth:'140px' }}>
      <div style={{ fontSize:'24px', marginBottom:'8px' }}>{emoji}</div>
      <div style={{ fontSize:'26px', fontWeight:'800', color }}>{value ?? '—'}</div>
      <div style={{ fontSize:'13px', fontWeight:'700', color:'#374151', marginTop:'2px' }}>{label}</div>
      {sub && <div style={{ fontSize:'11px', color:'#9ca3af', marginTop:'3px' }}>{sub}</div>}
    </div>
  );

  if (loading) return (
    <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center',
                  flexDirection:'column', gap:'12px', color:'#18181B' }}>
      <div style={{ fontSize:'36px' }}>📊</div>
      <p style={{ fontWeight:'700' }}>통계 불러오는 중...</p>
    </div>
  );

  return (
    <div style={{ flex:1, overflowY:'auto', background:'#f0f2f5', padding:'24px' }}>

      {/* 상단 */}
      <div style={{ display:'flex', alignItems:'center', gap:'14px', marginBottom:'24px', flexWrap:'wrap' }}>
        <button onClick={onBack}
          style={{ padding:'7px 14px', borderRadius:'8px', border:'1.5px solid #e5e7eb',
                   background:'#fff', fontSize:'13px', cursor:'pointer', fontWeight:'600' }}>
          ← 앱으로
        </button>
        <h1 style={{ fontSize:'22px', fontWeight:'800', color:'#18181B' }}>📊 관리자 대시보드</h1>
        <button onClick={loadStats}
          style={{ marginLeft:'auto', padding:'7px 14px', borderRadius:'8px', border:'none',
                   background:'#FF6F0F', color:'#fff', fontSize:'12px', fontWeight:'700', cursor:'pointer' }}>
          🔄 새로고침
        </button>
      </div>

      {/* 요약 카드 */}
      <div style={{ display:'flex', gap:'14px', flexWrap:'wrap', marginBottom:'24px' }}>
        {card('👥', '총 가입자', data.global.totalUsers?.toLocaleString(), '누적')}
        {card('🏃', '이번 달 활성', activeThisMonth?.toLocaleString(), `${thisMonth} 발주 저장 기준`, '#1e40af')}
        {card('📦', '이번 달 발주', thisMonthData.orderCount?.toLocaleString(), `${thisMonth}`, '#FF8F00')}
        {card('🥬', '등록 식자재', data.ingredients.length?.toLocaleString(), '전체 사용자 기준', '#FF6F0F')}
      </div>

      {/* 탭 */}
      <div style={{ display:'flex', gap:'6px', marginBottom:'16px', flexWrap:'wrap' }}>
        {[
          ['overview','📈 발주 추이'],
          ['ingredients','🥬 식자재 순위'],
          ['prices','💰 단가 현황'],
          ['assets','📊 데이터 자산'],
          ['allowlist','🔑 승인 관리'],
          ['migrate','🔄 데이터 이전'],
        ].map(([id,label]) => (
          <button key={id} onClick={() => { setTab(id); if (id === 'allowlist') loadUsers(); }}
            style={{ padding:'8px 18px', borderRadius:'8px', border:'none', cursor:'pointer',
                     fontSize:'13px', fontWeight:'700',
                     background: tab===id ? '#FF6F0F' : '#fff',
                     color: tab===id ? '#fff' : '#6b7280',
                     boxShadow: tab===id ? 'none' : '0 1px 3px rgba(0,0,0,0.08)',
                     position: 'relative' }}>
            {label}
          </button>
        ))}
      </div>

      {/* 탭 내용 */}
      {tab === 'overview' && (
        <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>

          {/* 월별 발주 추이 */}
          <div style={{ background:'#fff', borderRadius:'14px', padding:'22px',
                        border:'1px solid #e5e7eb', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize:'15px', fontWeight:'800', color:'#18181B', marginBottom:'20px' }}>
              📈 월별 발주 건수 (최근 6개월)
            </h3>
            {recentMonths.length === 0
              ? <p style={{ color:'#9ca3af', fontSize:'13px' }}>아직 데이터가 없어요</p>
              : <div style={{ display:'flex', alignItems:'flex-end', gap:'12px', height:'160px' }}>
                  {recentMonths.map(m => {
                    const pct = Math.max(((m.orderCount||0) / maxOrders) * 100, 2);
                    return (
                      <div key={m.month} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', height:'100%', justifyContent:'flex-end' }}>
                        <div style={{ fontSize:'12px', fontWeight:'700', color:'#18181B', marginBottom:'4px' }}>
                          {m.orderCount||0}
                        </div>
                        <div style={{ width:'100%', background:'#FF6F0F', borderRadius:'6px 6px 0 0',
                                      height:`${pct}%`, minHeight:'4px',
                                      opacity: m.month === thisMonth ? 1 : 0.5,
                                      transition:'height 0.3s' }} />
                        <div style={{ fontSize:'10px', color:'#9ca3af', marginTop:'6px', textAlign:'center' }}>
                          {m.month?.slice(5)}월
                        </div>
                      </div>
                    );
                  })}
                </div>
            }
          </div>

          {/* 전체 월별 테이블 */}
          <div style={{ background:'#fff', borderRadius:'14px', border:'1px solid #e5e7eb',
                        overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid #f3f4f6' }}>
              <h3 style={{ fontSize:'14px', fontWeight:'800', color:'#18181B' }}>전체 월별 현황</h3>
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#f9fafb' }}>
                  {['월','발주 건수','총 품목 수','활성 사용자'].map((h,i) => (
                    <th key={h} style={{ padding:'10px 16px', fontSize:'12px', fontWeight:'700',
                                         color:'#6b7280', textAlign: i===0?'left':'right' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...data.months].reverse().map((m,i) => (
                  <tr key={m.month} style={{ borderTop:'1px solid #f3f4f6',
                                              background: i%2===0?'#fff':'#fafafa' }}>
                    <td style={{ padding:'10px 16px', fontWeight:'700', color: m.month===thisMonth?'#FF6F0F':'#374151' }}>
                      {m.month} {m.month===thisMonth && <span style={{ fontSize:'10px', background:'#F4F4F5', color:'#18181B', padding:'1px 6px', borderRadius:'4px', marginLeft:'4px' }}>이번달</span>}
                    </td>
                    <td style={{ padding:'10px 16px', textAlign:'right', fontWeight:'700' }}>{(m.orderCount||0).toLocaleString()}</td>
                    <td style={{ padding:'10px 16px', textAlign:'right', color:'#6b7280' }}>{(m.totalItemCount||0).toLocaleString()}</td>
                    <td style={{ padding:'10px 16px', textAlign:'right', color:'#1e40af' }}>
                      {(data.global[`activeUsers_${m.month}`]||0).toLocaleString()}
                    </td>
                  </tr>
                ))}
                {data.months.length === 0 && (
                  <tr><td colSpan={4} style={{ padding:'30px', textAlign:'center', color:'#9ca3af' }}>
                    아직 데이터가 없어요
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'ingredients' && (
        <div style={{ background:'#fff', borderRadius:'14px', border:'1px solid #e5e7eb',
                      overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ padding:'16px 20px', borderBottom:'1px solid #f3f4f6', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <h3 style={{ fontSize:'14px', fontWeight:'800', color:'#18181B' }}>
              🥬 인기 식자재 순위 (전체 {data.ingredients.length}종)
            </h3>
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#1A56A0', color:'#fff' }}>
                {['순위','식자재명','총 발주 횟수','총 발주량','마지막 발주'].map((h,i) => (
                  <th key={h} style={{ padding:'10px 14px', fontSize:'12px', fontWeight:'700',
                                       textAlign: i<=1?'left':'right' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.ingredients.slice(0,30).map((ing, i) => (
                <tr key={ing.id} style={{ borderTop:'1px solid #f3f4f6', background: i%2===0?'#fff':'#fafafa' }}>
                  <td style={{ padding:'10px 14px', fontWeight:'800', color: i<3?'#FF8F00':'#9ca3af', fontSize:'13px' }}>
                    {i===0?'🥇':i===1?'🥈':i===2?'🥉':`#${i+1}`}
                  </td>
                  <td style={{ padding:'10px 14px', fontWeight:'700', fontSize:'14px' }}>{ing.name}</td>
                  <td style={{ padding:'10px 14px', textAlign:'right', fontWeight:'700', color:'#18181B' }}>
                    {(ing.orderCount||0).toLocaleString()}회
                  </td>
                  <td style={{ padding:'10px 14px', textAlign:'right', color:'#6b7280' }}>
                    {ing.totalQty ? `${Number(ing.totalQty).toLocaleString()}` : '—'}
                  </td>
                  <td style={{ padding:'10px 14px', textAlign:'right', fontSize:'12px', color:'#9ca3af' }}>
                    {ing.lastSeen || '—'}
                  </td>
                </tr>
              ))}
              {data.ingredients.length === 0 && (
                <tr><td colSpan={5} style={{ padding:'30px', textAlign:'center', color:'#9ca3af' }}>
                  아직 데이터가 없어요
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'assets' && (
        <div style={{ background:'#fff', borderRadius:'14px', padding:'20px', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
          <AdminDataAssets user={user} />
        </div>
      )}

      {tab === 'allowlist' && (() => {
        // ── V107: 승인 화면 단일화 ──────────────────────────────────────
        // 예전엔 승인 관리 / 승인 신청 / 가입자 세 탭이 각각 다른 컬렉션을
        // 보여줘서 "승인했는데 왜 저기 그대로지?"가 됐다. 하나로 합쳤다.
        //   위 = 승인 대기, 아래 = 승인됨. 승인 버튼을 누르면 아래로 내려간다.
        const norm = (e) => String(e || '').trim().toLowerCase();
        const pending  = users.filter(u => u.email && !isApproved(u.email));
        const approved = users.filter(u => u.email && isApproved(u.email));
        // 승인 목록에는 있는데 아직 가입 안 한 이메일 (미리 승인해둔 사람)
        const signedUp = new Set(users.map(u => norm(u.email)).filter(Boolean));
        const orphans  = (allowlist.emails || [])
          .filter(e => e && !signedUp.has(norm(e)) && norm(e) !== norm(ADMIN_EMAIL));

        const planButtons = (u) => (
          <div style={{ display:'flex', gap:4 }}>
            {PLAN_OPTIONS.map(pl => {
              const cur = u.subscription?.status === 'active' && u.subscription?.plan;
              const on = cur === pl.value;
              return (
                <button key={pl.value} onClick={() => updateUserPlan(u.uid, pl.value)}
                  style={{ padding:'4px 8px', borderRadius:6, cursor:'pointer',
                           fontSize:11, fontWeight:700,
                           border: on ? `2px solid ${pl.color}` : '1px solid #e5e7eb',
                           background: on ? pl.color : '#fff',
                           color: on ? '#fff' : pl.color }}>
                  {pl.label}
                </button>
              );
            })}
          </div>
        );

        const userRow = (u, isPend) => (
          <div key={u.uid} style={{
            display:'flex', alignItems:'center', gap:10, flexWrap:'wrap',
            padding:'12px 14px', borderRadius:10,
            background: isPend ? '#FFF7ED' : '#f9fafb',
            border: `1px solid ${isPend ? '#FED7AA' : '#e5e7eb'}`,
          }}>
            <div style={{ flex:1, minWidth:180 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                <span style={{ fontSize:14, fontWeight:700, color:'#18181B' }}>
                  {u.storeName || u.displayName || '(이름 없음)'}
                </span>
                {Array.isArray(u.managedOwnerUids) && u.managedOwnerUids.length > 0 && (
                  <span style={{ fontSize:10, fontWeight:800, padding:'2px 7px', borderRadius:6,
                                 background:'#EFF6FF', color:'#1A56A0' }}>
                    매니저 {u.managedOwnerUids.length}곳
                  </span>
                )}
              </div>
              <div style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>{u.email}</div>
            </div>

            {planButtons(u)}

            {u.email === ADMIN_EMAIL ? (
              <span style={{ padding:'5px 10px', borderRadius:8, background:'#FF6F0F',
                             color:'#fff', fontSize:11, fontWeight:800 }}>관리자</span>
            ) : isPend ? (
              <button onClick={() => approveUser(u.email)} disabled={allowSaving}
                style={{ padding:'6px 16px', borderRadius:8, border:'none', cursor:'pointer',
                         background:'#16A34A', color:'#fff', fontSize:12, fontWeight:800 }}>
                ✓ 승인
              </button>
            ) : (
              <button onClick={() => revokeUser(u.email)} disabled={allowSaving}
                style={{ padding:'6px 12px', borderRadius:8, border:'1px solid #e5e7eb',
                         background:'#fff', color:'#52525B', fontSize:12, fontWeight:700,
                         cursor:'pointer' }}>
                차단
              </button>
            )}

            {u.email !== ADMIN_EMAIL && (
              <button onClick={() => purgeUserData(u)} disabled={purging === u.uid}
                title="이 사람의 단가표·레시피·거래장부를 모두 삭제 (복구 불가)"
                style={{ padding:'6px 10px', borderRadius:8, border:'1px solid #FECACA',
                         background:'#fff5f5', color:'#B91C1C', fontSize:12, fontWeight:700,
                         cursor: purging === u.uid ? 'default' : 'pointer' }}>
                {purging === u.uid ? '삭제 중...' : '데이터 삭제'}
              </button>
            )}
          </div>
        );

        return (
        <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>

          {/* 접근 모드 */}
          <div style={{ background:'#fff', borderRadius:'14px', padding:'20px 24px',
                        border:'1px solid #e5e7eb', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
              <div>
                <h3 style={{ fontSize:'15px', fontWeight:'800', color:'#18181B', marginBottom:'4px' }}>접근 모드</h3>
                <p style={{ fontSize:'13px', color:'#6b7280' }}>
                  {allowlist.open
                    ? '🟢 오픈 모드 — 누구나 가입 및 로그인 가능'
                    : '🔴 승인 모드 — 아래에서 승인한 사람만 로그인 가능'}
                </p>
              </div>
              <button
                onClick={() => saveAllowlist({ ...allowlist, open: !allowlist.open })}
                disabled={allowSaving}
                style={{
                  padding:'10px 20px', borderRadius:'10px', border:'none', cursor:'pointer',
                  fontWeight:'800', fontSize:'13px',
                  background: allowlist.open ? '#52525B' : '#FF6F0F',
                  color:'#fff', opacity: allowSaving ? 0.6 : 1,
                }}>
                {allowlist.open ? '승인 모드로 전환' : '오픈 모드로 전환'}
              </button>
            </div>
          </div>

          {/* 가입 승인 — 단일 목록 */}
          <div style={{ background:'#fff', borderRadius:'14px', padding:'20px 24px',
                        border:'1px solid #e5e7eb', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                          flexWrap:'wrap', gap:8, marginBottom:6 }}>
              <h3 style={{ fontSize:'15px', fontWeight:'800', color:'#18181B' }}>🔑 가입 승인</h3>
              <button onClick={loadUsers} disabled={usersLoading}
                style={{ padding:'6px 12px', borderRadius:8, border:'1px solid #e5e7eb',
                         background:'#fff', color:'#52525B', fontSize:12, fontWeight:700,
                         cursor:'pointer', opacity: usersLoading ? 0.6 : 1 }}>
                {usersLoading ? '불러오는 중...' : '🔄 새로고침'}
              </button>
            </div>
            <p style={{ fontSize:12, color:'#6b7280', lineHeight:1.6, marginBottom:18 }}>
              여기서 승인한 사람이 <strong>한 매장의 사장</strong>이 됩니다.
              사장이 자기 설정에서 초대한 매니저·직원은 <strong>여기 승인 없이</strong> 바로 들어옵니다.
            </p>

            {/* 승인 대기 */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:13, fontWeight:800, color:'#9A3412', marginBottom:8 }}>
                승인 대기 ({pending.length}명)
              </div>
              {pending.length === 0 ? (
                <div style={{ fontSize:13, color:'#9ca3af', padding:'10px 0' }}>대기 중인 가입자가 없습니다</div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {pending.map(u => userRow(u, true))}
                </div>
              )}
            </div>

            {/* 승인됨 */}
            <div>
              <div style={{ fontSize:13, fontWeight:800, color:'#16A34A', marginBottom:8 }}>
                승인됨 ({approved.length + orphans.length}명)
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {approved.map(u => userRow(u, false))}
                {orphans.map(email => (
                  <div key={email} style={{
                    display:'flex', alignItems:'center', gap:10, flexWrap:'wrap',
                    padding:'12px 14px', borderRadius:10, background:'#f9fafb',
                    border:'1px dashed #d4d4d8',
                  }}>
                    <div style={{ flex:1, minWidth:180 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'#71717A' }}>미가입 (미리 승인)</div>
                      <div style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>{email}</div>
                    </div>
                    <button onClick={() => removeEmail(email)} disabled={allowSaving}
                      style={{ padding:'6px 12px', borderRadius:8, border:'1px solid #e5e7eb',
                               background:'#fff', color:'#52525B', fontSize:12, fontWeight:700,
                               cursor:'pointer' }}>
                      삭제
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* 미리 승인 */}
            <div style={{ marginTop:20, paddingTop:16, borderTop:'1px solid #f4f4f5' }}>
              <div style={{ fontSize:12, color:'#6b7280', marginBottom:8 }}>
                아직 가입하지 않은 사람을 미리 승인해둘 수 있어요
              </div>
              <div style={{ display:'flex', gap:'8px' }}>
                <input
                  type="email"
                  placeholder="이메일 입력"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addEmail(); }}
                  style={{ flex:1, padding:'10px 14px', borderRadius:'8px',
                           border:'1px solid #e5e7eb', fontSize:'14px', outline:'none' }}
                />
                <button onClick={addEmail} disabled={allowSaving}
                  style={{ padding:'10px 20px', borderRadius:'8px', border:'none', cursor:'pointer',
                           background:'#FF6F0F', color:'#fff', fontSize:'13px', fontWeight:'700',
                           opacity: allowSaving ? 0.6 : 1 }}>
                  추가
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}


      {tab === 'migrate' && (
        <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>

          {/* 안내 */}
          <div style={{ background:'#FAFAFA', border:'1px solid #fde68a', borderRadius:'14px', padding:'18px 22px' }}>
            <h3 style={{ fontSize:'14px', fontWeight:'800', color:'#52525B', marginBottom:'8px' }}>📋 데이터 이전 방법</h3>
            <p style={{ fontSize:'13px', color:'#78350f', lineHeight:1.8 }}>
              <strong>① 이메일 계정에서 내보내기</strong><br/>
              이메일/비밀번호로 로그아웃 → 이메일 계정 로그인 → /admin → "내 데이터 내보내기" 클릭 → JSON 파일 저장<br/><br/>
              <strong>② 구글 계정으로 가져오기</strong><br/>
              로그아웃 → 구글 계정 로그인 → /admin → "JSON 파일 가져오기" 클릭 → 저장한 파일 선택
            </p>
          </div>

          {/* 내보내기 */}
          <div style={{ background:'#fff', borderRadius:'14px', padding:'22px 24px',
                        border:'1px solid #e5e7eb', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize:'15px', fontWeight:'800', color:'#18181B', marginBottom:'14px' }}>
              📤 내 데이터 내보내기
            </h3>
            <p style={{ fontSize:'13px', color:'#6b7280', marginBottom:'14px' }}>
              현재 로그인된 계정({user?.email})의 레시피, 단가표, 발주 내역을 JSON 파일로 저장합니다.
            </p>
            <button
              onClick={async () => {
                setMigrateStatus('내보내는 중...');
                try {
                  const snap = await getDoc(doc(db, 'users', user.uid));
                  if (!snap.exists()) { setMigrateStatus('❌ Firestore에 데이터가 없습니다.'); return; }
                  const raw = snap.data();
                  const exportObj = {
                    _exportedAt: new Date().toISOString(),
                    _email: user.email,
                    categories:   raw.categories   || [],
                    prices:       raw.prices       || [],
                    orderHistory: raw.orderHistory || [],
                    profitLoss:   raw.profitLoss   || {},
                    taxData:      raw.taxData      || {},
                    inventory:    raw.inventory    || {},
                  };
                  const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' });
                  const url  = URL.createObjectURL(blob);
                  const a    = document.createElement('a');
                  a.href     = url;
                  a.download = `semfit_backup_${new Date().toISOString().slice(0,10)}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                  setMigrateStatus('✅ 내보내기 완료! 파일이 다운로드되었습니다.');
                } catch(e) {
                  setMigrateStatus('❌ 오류: ' + e.message);
                }
              }}
              style={{ padding:'12px 24px', borderRadius:'10px', border:'none', cursor:'pointer',
                       background:'#FF6F0F', color:'#fff', fontWeight:'800', fontSize:'14px' }}>
              📤 JSON 파일로 내보내기
            </button>
          </div>

          {/* 가져오기 */}
          <div style={{ background:'#fff', borderRadius:'14px', padding:'22px 24px',
                        border:'1px solid #e5e7eb', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize:'15px', fontWeight:'800', color:'#1e40af', marginBottom:'14px' }}>
              📥 JSON 파일 가져오기
            </h3>
            <p style={{ fontSize:'13px', color:'#6b7280', marginBottom:'14px' }}>
              이전에 내보낸 JSON 파일을 선택하면 현재 계정({user?.email})의 Firestore에 데이터를 덮어씁니다.
            </p>
            <input
              type="file"
              accept=".json"
              onChange={e => {
                const file = e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = ev => {
                  try {
                    const parsed = JSON.parse(ev.target.result);
                    setImportData(parsed);
                    setImportPreview({
                      email:        parsed._email || '알 수 없음',
                      exportedAt:   parsed._exportedAt ? parsed._exportedAt.slice(0,10) : '알 수 없음',
                      recipeCount:  (parsed.categories || []).flatMap(c=>c.items||[]).length,
                      priceCount:   (parsed.prices || []).filter(p=>!p.isSemi).length,
                      orderCount:   (parsed.orderHistory || []).length,
                    });
                    setMigrateStatus('');
                  } catch(err) {
                    setMigrateStatus('❌ 파일 형식이 올바르지 않습니다.');
                    setImportData(null); setImportPreview(null);
                  }
                };
                reader.readAsText(file);
              }}
              style={{ marginBottom:'12px' }}
            />
            {importPreview && (
              <div style={{ background:'#FAFAFA', border:'1px solid #F4F4F5', borderRadius:'10px',
                            padding:'14px 16px', marginBottom:'14px', fontSize:'13px' }}>
                <p style={{ fontWeight:'800', color:'#18181B', marginBottom:'6px' }}>📋 가져올 데이터 미리보기</p>
                <p>원본 계정: <strong>{importPreview.email}</strong></p>
                <p>내보낸 날짜: {importPreview.exportedAt}</p>
                <p>레시피 수: <strong>{importPreview.recipeCount}개</strong></p>
                <p>단가 항목: <strong>{importPreview.priceCount}개</strong></p>
                <p>발주 내역: <strong>{importPreview.orderCount}건</strong></p>
              </div>
            )}
            {importData && (
              <button
                onClick={async () => {
                  if (!window.confirm(`현재 계정(${user?.email})의 데이터를 가져온 데이터로 덮어씁니다. 계속하시겠습니까?`)) return;
                  setMigrateStatus('가져오는 중...');
                  try {
                    await setDoc(doc(db, 'users', user.uid), {
                      categories:   importData.categories   || [],
                      prices:       importData.prices       || [],
                      orderHistory: importData.orderHistory || [],
                      profitLoss:   importData.profitLoss   || {},
                      taxData:      importData.taxData      || {},
                      inventory:    importData.inventory    || {},
                    }, { merge: false });
                    setMigrateStatus('✅ 가져오기 완료! 앱으로 돌아가면 데이터가 적용됩니다.');
                    setImportData(null); setImportPreview(null);
                  } catch(e) {
                    setMigrateStatus('❌ 오류: ' + e.message);
                  }
                }}
                style={{ padding:'12px 24px', borderRadius:'10px', border:'none', cursor:'pointer',
                         background:'#1e40af', color:'#fff', fontWeight:'800', fontSize:'14px' }}>
                📥 이 데이터로 가져오기
              </button>
            )}
          </div>

          {/* 상태 메시지 */}
          {migrateStatus && (
            <div style={{
              padding:'14px 18px', borderRadius:'10px', fontSize:'14px', fontWeight:'700',
              background: migrateStatus.startsWith('✅') ? '#FAFAFA' : migrateStatus.startsWith('❌') ? '#F4F4F5' : '#FAFAFA',
              color:      migrateStatus.startsWith('✅') ? '#FF6F0F' : migrateStatus.startsWith('❌') ? '#52525B' : '#52525B',
              border: `1px solid ${migrateStatus.startsWith('✅') ? '#E4E4E7' : migrateStatus.startsWith('❌') ? '#E4E4E7' : '#fde68a'}`,
            }}>
              {migrateStatus}
            </div>
          )}
        </div>
      )}


      {tab === 'prices' && (
        <div style={{ background:'#fff', borderRadius:'14px', border:'1px solid #e5e7eb',
                      overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ padding:'16px 20px', borderBottom:'1px solid #f3f4f6' }}>
            <h3 style={{ fontSize:'14px', fontWeight:'800', color:'#18181B' }}>
              💰 식자재 단가 현황 (사용자 입력 기준 최근값)
            </h3>
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#f9fafb' }}>
                {['식자재명','최근 단가','발주 횟수','마지막 발주'].map((h,i) => (
                  <th key={h} style={{ padding:'10px 14px', fontSize:'12px', fontWeight:'700',
                                       color:'#6b7280', textAlign: i===0?'left':'right' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...data.ingredients]
                .filter(i => i.lastPrice > 0)
                .sort((a,b) => (b.lastPrice||0) - (a.lastPrice||0))
                .map((ing, i) => (
                  <tr key={ing.id} style={{ borderTop:'1px solid #f3f4f6', background: i%2===0?'#fff':'#fafafa' }}>
                    <td style={{ padding:'10px 14px', fontWeight:'700' }}>{ing.name}</td>
                    <td style={{ padding:'10px 14px', textAlign:'right', fontWeight:'800', color:'#FF8F00', fontSize:'14px' }}>
                      {Number(ing.lastPrice||0).toLocaleString()}원
                    </td>
                    <td style={{ padding:'10px 14px', textAlign:'right', color:'#6b7280' }}>
                      {(ing.orderCount||0).toLocaleString()}회
                    </td>
                    <td style={{ padding:'10px 14px', textAlign:'right', fontSize:'12px', color:'#9ca3af' }}>
                      {ing.lastSeen || '—'}
                    </td>
                  </tr>
              ))}
              {data.ingredients.filter(i=>i.lastPrice>0).length === 0 && (
                <tr><td colSpan={4} style={{ padding:'30px', textAlign:'center', color:'#9ca3af' }}>
                  아직 데이터가 없어요
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// 관리자 여부 체크 헬퍼
export const isAdmin = (user) => user?.email === ADMIN_EMAIL;

export default AdminDashboard;
